'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { JumpMenuItem } from '@/hooks/useGuideData';

interface JumpNavProps {
  jumpMenu: JumpMenuItem[];
  /** Sidebar heading. Pass null to hide. Defaults to "In this guide". */
  title?: string | null;
}

interface NavGroup {
  section: JumpMenuItem | null;
  items: JumpMenuItem[];
}

// Group the flat jump menu into sections. Links that appear before the first
// section (or in menus with no sections at all) live in a section-less group.
function buildGroups(jumpMenu: JumpMenuItem[]): NavGroup[] {
  const groups: NavGroup[] = [];
  let current: NavGroup | null = null;

  for (const item of jumpMenu) {
    if (item.type === 'section') {
      current = { section: item, items: [] };
      groups.push(current);
    } else {
      if (!current) {
        current = { section: null, items: [] };
        groups.push(current);
      }
      current.items.push(item);
    }
  }

  return groups;
}

function LinkItem({
  item,
  active,
  onNavigate,
}: {
  item: JumpMenuItem;
  active: boolean;
  onNavigate: (anchor: string) => void;
}) {
  return (
    <a
      href={`#${item.anchor}`}
      onClick={() => onNavigate(item.anchor)}
      className={cn(
        'w-full text-left px-3 py-2 rounded-lg transition-colors block',
        active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
      )}
    >
      {item.label}
    </a>
  );
}

export function JumpNav({ jumpMenu, title = 'In this guide' }: JumpNavProps) {
  const groups = useMemo(() => buildGroups(jumpMenu), [jumpMenu]);
  const anchors = useMemo(() => jumpMenu.map((item) => item.anchor), [jumpMenu]);

  const [activeAnchor, setActiveAnchor] = useState(jumpMenu[0]?.anchor || '');
  // null => auto mode (expansion follows the section currently being read).
  // A Set => the user manually toggled sections; we keep their choice until
  // the next scroll hands control back to auto mode (desktop only).
  const [manualExpanded, setManualExpanded] = useState<Set<string> | null>(null);
  // Auto-expand only applies on the desktop sticky layout. On the stacked
  // mobile layout the menu lives at the top, so sections are manual-only.
  const [isDesktop, setIsDesktop] = useState(false);

  // While a click-driven jump is animating we lock out the scroll spy so the
  // target section doesn't collapse/re-expand as the page scrolls past others.
  const navigatingRef = useRef(false);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The section that contains the anchor currently in view (auto-expand target).
  const activeSectionAnchor = useMemo(() => {
    for (const group of groups) {
      if (!group.section) continue;
      if (
        group.section.anchor === activeAnchor ||
        group.items.some((item) => item.anchor === activeAnchor)
      ) {
        return group.section.anchor;
      }
    }
    return groups.find((group) => group.section)?.section?.anchor ?? '';
  }, [groups, activeAnchor]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const updateActive = () => {
      const offset = 120;
      let current = anchors[0] || '';
      for (const anchor of anchors) {
        const element = document.getElementById(anchor);
        if (!element) continue;
        if (element.getBoundingClientRect().top - offset <= 0) {
          current = anchor;
        } else {
          break;
        }
      }
      if (current) setActiveAnchor(current);
    };

    const handleScroll = () => {
      // A click-driven jump is in progress: keep the target section expanded
      // and only resume the spy once scrolling has settled.
      if (navigatingRef.current) {
        clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = setTimeout(() => {
          navigatingRef.current = false;
          updateActive();
        }, 150);
        return;
      }

      // Scrolling hands control back to auto mode (desktop only).
      if (isDesktop) setManualExpanded(null);
      updateActive();
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollEndTimerRef.current);
    };
  }, [anchors, isDesktop]);

  const isExpanded = (sectionAnchor: string) => {
    if (!isDesktop) return manualExpanded?.has(sectionAnchor) ?? false;
    if (manualExpanded) return manualExpanded.has(sectionAnchor);
    return sectionAnchor === activeSectionAnchor;
  };

  const toggleSection = (sectionAnchor: string) => {
    setManualExpanded((prev) => {
      const seed = prev ?? (isDesktop && activeSectionAnchor ? [activeSectionAnchor] : []);
      const next = new Set(seed);
      if (next.has(sectionAnchor)) next.delete(sectionAnchor);
      else next.add(sectionAnchor);
      return next;
    });
  };

  // Clicking a jump link: pin the active section to the target and suppress the
  // scroll spy until the smooth scroll finishes, avoiding a jumpy re-expand.
  const handleNavigate = (anchor: string) => {
    if (!isDesktop) return;
    navigatingRef.current = true;
    setManualExpanded(null);
    setActiveAnchor(anchor);
  };

  if (jumpMenu.length === 0) {
    return null;
  }

  return (
    <>
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <div className="w-full flex flex-col flex-1 md:border-r border-zinc-700 border-opacity-50 md:pr-16">
        <div className="sticky top-6">
          {title ? (
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-6">{title}</h3>
          ) : null}
          <nav className="space-y-2 md:min-w-[300px]">
            {groups.map((group, idx) => {
              if (!group.section) {
                return group.items.map((item) => (
                  <LinkItem
                    key={item.anchor}
                    item={item}
                    active={activeAnchor === item.anchor}
                    onNavigate={handleNavigate}
                  />
                ));
              }

              const expanded = isExpanded(group.section.anchor);
              const sectionActive =
                activeAnchor === group.section.anchor ||
                group.items.some((item) => item.anchor === activeAnchor);
              const hasItems = group.items.length > 0;

              return (
                <div key={group.section.anchor} className={cn(idx > 0 && 'pt-2')}>
                  <button
                    type="button"
                    onClick={() => hasItems && toggleSection(group.section!.anchor)}
                    aria-expanded={hasItems ? expanded : undefined}
                    className={cn(
                      'flex items-center justify-between w-full gap-2 px-3 pb-3 text-left font-bold uppercase tracking-wider border-b border-zinc-700 border-opacity-50 transition-colors',
                      sectionActive ? 'text-white' : 'text-zinc-500 hover:text-white',
                      !hasItems && 'cursor-default'
                    )}
                  >
                    <span>{group.section.label}</span>
                    {hasItems && (
                      <ChevronDownIcon
                        className={cn(
                          'w-4 h-4 flex-shrink-0 transition-transform duration-200',
                          expanded ? 'rotate-180' : 'rotate-0'
                        )}
                      />
                    )}
                  </button>
                  {hasItems && (
                    <div
                      className={cn(
                        'grid transition-all duration-200',
                        expanded
                          ? 'grid-rows-[1fr] opacity-100 mt-2'
                          : 'grid-rows-[0fr] opacity-0'
                      )}
                    >
                      <div className="overflow-hidden">
                        <div className="space-y-1">
                          {group.items.map((item) => (
                            <LinkItem
                              key={item.anchor}
                              item={item}
                              active={activeAnchor === item.anchor}
                              onNavigate={handleNavigate}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}
